import { area, bbox, bboxPolygon, polygon, simplify, truncate } from '@turf/turf'

import { MAPBOX_ACCESS_TOKEN, MAPBOX_API } from '@/config/mapbox'

// Project boundaries have no stored thumbnail, so previews are Mapbox Static
// Images URLs built from the boundary geometry itself.

const STYLE = 'light-v11'
const DEFAULT_COLOR = '#0059ff'
const MAX_URL_LENGTH = 8000
// Country-sized outlines only fit the URL at the coarser tolerances.
const SIMPLIFY_STEPS = [0.001, 0.01, 0.05, 0.1, 0.25]

export interface StaticMapOptions {
  width?: number
  height?: number
  /** Drop small outlying parts; for Place outlines, not project boundaries. */
  mainPartsOnly?: boolean
}

const urlCache = new Map<string, string | null>()

const coordsAreAllZero = (coords: any): boolean => {
  if (typeof coords[0] === 'number') return coords[0] === 0 && coords[1] === 0
  return coords.every((c: any) => coordsAreAllZero(c))
}

// The API substitutes a degenerate [[[0,0],[0,0],[0,0],[0,0]]] polygon for a
// NULL geometry, so a boundary can exist (place details only) with nothing to draw.
export const hasRealGeometry = (feature: any): boolean => {
  const coords = feature?.geometry?.coordinates
  if (!coords?.length) return false
  return !coordsAreAllZero(coords)
}

// Islands and overseas territories (Samoa, the Aleutians across the antimeridian)
// would frame half the globe around a country; keep parts at least 1% of the largest.
const MAIN_PART_SHARE = 0.01

const keepMainParts = (feature: any) => {
  if (feature.geometry?.type !== 'MultiPolygon') return feature
  const parts = feature.geometry.coordinates.map((coords: any) => ({ coords, size: area(polygon(coords)) }))
  const largest = Math.max(...parts.map((part: any) => part.size))
  const coordinates = parts.filter((part: any) => part.size >= largest * MAIN_PART_SHARE).map((part: any) => part.coords)
  return { ...feature, geometry: { type: 'MultiPolygon', coordinates } }
}

const overlayFeature = (geometry: any, color: string) => ({
  type: 'Feature',
  properties: {
    stroke: color,
    'stroke-width': 2,
    'stroke-opacity': 1,
    fill: color,
    'fill-opacity': 0.3
  },
  geometry
})

const buildUrl = (geometry: any, color: string, width: number, height: number): string => {
  const overlay = encodeURIComponent(JSON.stringify(overlayFeature(geometry, color)))
  return `${MAPBOX_API.STATIC}/${STYLE}/static/geojson(${overlay})/auto/${width}x${height}@2x` +
    `?padding=24&access_token=${MAPBOX_ACCESS_TOKEN}`
}

export const buildStaticMapUrl = (feature: any, options: StaticMapOptions = {}): string | null => {
  if (!hasRealGeometry(feature)) return null

  const width = options.width ?? 320
  const height = options.height ?? 180
  const color = feature.properties?.color || DEFAULT_COLOR
  // An edited shape keeps its project id or Place key, so the key also fingerprints the geometry.
  const shape = `${bbox(feature).join(',')}:${JSON.stringify(feature.geometry).length}`
  const cacheKey = `${feature.properties?.project_id ?? feature.properties?.place}:${width}x${height}:${color}:${!!options.mainPartsOnly}:${shape}`
  if (urlCache.has(cacheKey)) return urlCache.get(cacheKey)!

  let url: string | null = null

  try {
    const source = options.mainPartsOnly ? keepMainParts(feature) : feature
    const trimmed = truncate(source, { precision: 4, mutate: false })
    const candidates = [
      () => trimmed.geometry,
      ...SIMPLIFY_STEPS.map((tolerance) => () =>
        simplify(trimmed, { tolerance, highQuality: false, mutate: false }).geometry),
      // Last resort: the bounding rectangle always fits in a URL.
      () => bboxPolygon(bbox(trimmed) as any).geometry
    ]

    for (const build of candidates) {
      let candidate: string
      try {
        candidate = buildUrl(build(), color, width, height)
      } catch {
        // simplify() rejects some geometry types (points) — try the next fallback.
        continue
      }
      if (candidate.length <= MAX_URL_LENGTH) {
        url = candidate
        break
      }
    }
  } catch (e) {
    console.error('Failed to build static map url', e)
  }

  urlCache.set(cacheKey, url)
  return url
}
